import React, { useMemo } from 'react'
import { CircularLoader } from '@st-operations/uikit/src/v2'
import format from '@st-operations/uikit/lib/format.js'
import { scaleLog } from 'd3-scale'

import { useLazyLoad } from '_hooks'
import { TIME_INTERVAL_VALUES } from '_components/interval-select/index.jsx'
import useEventsChartDataConverter from '../../hooks/useEventsChartDataConverter'
import { SCALES } from '_components/chart-scale-select/index.jsx'
import twoNonZeroDecimals from '_lib/twoNonZeroDecimals'

import './styles.scss'

const logScaleFunc = scaleLog().base(Math.E)

const LINE_COLORS = [
  '#5BAC4D',
  '#FF2710',
  '#7DE2D3',
  '#0CB2B2',
  '#146A59',
  '#91D400',
  '#B581A2',
  '#34158C',
  '#A40965',
  '#F7DB71',
  '#13682B',
  '#7D69B5',
]

const monthNames = [
  'Jan.',
  'Feb.',
  'Mar.',
  'Apr.',
  'May',
  'Jun.',
  'Jul.',
  'Aug.',
  'Sept.',
  'Oct.',
  'Nov.',
  'Dec.',
]

const LOG_SCALE_DATA_MIN = 0.99999

function buildLogTicks(min, max, scale) {
  let f = Math.pow(max / min, 1 / --scale),
    result = [min]

  while (--scale) {
    result.push(result[result.length - 1] * f)
  }
  result.push(max)
  return result
}

const LineTrendsChart = ({
  loading,
  trendsData = [],
  trendsDataKeys,
  disabledKeywords = [],
  scale,
  hoveredKeyword,
  interval,
  emptyMessage = "There's no data to show",
  onHoveredKeywordChange = () => {},
  transformKeyword = (kw) => kw,
}) => {
  const data = useEventsChartDataConverter(trendsData)

  const customTooltipOnYourLine = (e) => {
    if (e.active && e.payload !== null && e.payload[0] !== null) {
      const val = e.payload[0].payload[hoveredKeyword]
      const absVal = Math.abs(val, 10) * 100
      return (
        <If condition={hoveredKeyword}>
          <div className="line-trends-chart__tooltip">
            <div>{e.payload[0].payload['date'].toLocaleDateString()}</div>
            <div>
              {transformKeyword(hoveredKeyword)}:{' '}
              {`${
                (Math.sign(val) < 0 ? '-' : '') +
                (absVal < 1000 ? twoNonZeroDecimals(absVal) : format('metric', absVal))
              }%`}
            </div>
          </div>
        </If>
      )
    }
    return null
  }

  const dataMax = useMemo(
    () =>
      Object.keys(trendsData || []).reduce(
        (total, key) =>
          Math.max(
            total,
            Object.keys(trendsData[key] || []).reduce(
              (max, date) => Math.max(max, trendsData[key][date].change),
              LOG_SCALE_DATA_MIN
            )
          ),
        LOG_SCALE_DATA_MIN
      ),
    [trendsData]
  )

  const [
    { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis },
    loadingRecharts,
  ] = useLazyLoad(import('recharts'))

  return (
    <section className="line-trends-chart__chart">
      <If condition={!loading && !loadingRecharts}>
        <If condition={data.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              width={500}
              height={300}
              data={
                scale === SCALES.LOGARITHMIC
                  ? data.map((item) =>
                      Object.keys(item).reduce((obj, key) => ({
                        ...obj,
                        [key]: key !== 'date' ? Math.max(item[key], LOG_SCALE_DATA_MIN) : item[key],
                      }))
                    )
                  : data
              }
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 50,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(val) =>
                  interval === TIME_INTERVAL_VALUES.DAILY
                    ? val.toLocaleDateString()
                    : interval === TIME_INTERVAL_VALUES.WEEKLY
                    ? val.toLocaleDateString()
                    : interval === TIME_INTERVAL_VALUES.MONTHLY
                    ? `${monthNames[val.getMonth()]} ${val.getFullYear()}`
                    : ''
                }
                angle={-70}
                textAnchor="end"
                label={{
                  value: 'Date',
                  position: 'bottom',
                  offset: 10,
                  style: { textAnchor: 'middle' },
                }}
                height={80}
              />
              <YAxis
                scale={scale === SCALES.LOGARITHMIC ? logScaleFunc : undefined}
                domain={['dataMin', scale === SCALES.LOGARITHMIC ? dataMax : 'dataMax']}
                ticks={
                  scale === SCALES.LOGARITHMIC
                    ? buildLogTicks(LOG_SCALE_DATA_MIN, dataMax, 5)
                    : undefined
                }
                tickFormatter={(val) => {
                  const absVal = Math.abs(val, 10) * 100
                  return `${
                    (Math.sign(val) < 0 ? '-' : '') +
                    (absVal < 1000 ? twoNonZeroDecimals(absVal) : format('metric', absVal))
                  }%`
                }}
                label={{
                  value: 'Percent Change in Events',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 0,
                  style: { textAnchor: 'middle' },
                }}
                width={70}
              />
              <For
                each="lineName"
                index="index"
                of={
                  trendsDataKeys ||
                  Object.keys(trendsData || {})
                    .sort((k1) => (!disabledKeywords.includes(k1) ? -1 : 1))
                    .sort((k1) => (k1 === hoveredKeyword ? 1 : -1))
                }
              >
                <If condition={!disabledKeywords.includes(lineName)}>
                  <Line
                    type="monotone"
                    dataKey={lineName}
                    stroke={
                      // (disabledKeywords.includes(lineName) && hoveredKeyword !== lineName) ||
                      hoveredKeyword && hoveredKeyword !== lineName
                        ? '#eee'
                        : LINE_COLORS[
                            (trendsDataKeys || Object.keys(trendsData || {})).findIndex(
                              (f) => f === lineName
                            )
                          ] || 'black'
                    }
                    strokeWidth={2}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                    onMouseEnter={() => onHoveredKeywordChange(lineName)}
                    onMouseLeave={() => onHoveredKeywordChange(undefined)}
                    style={{ cursor: 'pointer' }}
                  />
                </If>
              </For>
              <ReferenceLine y={0} stroke="#000000" />
              <Tooltip content={customTooltipOnYourLine} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </If>
        <If condition={data.length === 0}>{emptyMessage}</If>
      </If>
      <If condition={loading}>
        <CircularLoader className="line-trends-chart__loading" />
      </If>
    </section>
  )
}

export default LineTrendsChart
